import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

axios.defaults.withCredentials = false;

// Subscribers for auth changes (CartProvider listens to this).
const subscribers = new Set();
export const subscribeAuthChange = (fn) => {
  subscribers.add(fn);
  return () => subscribers.delete(fn);
};
const notifyAuthChange = (user) => {
  subscribers.forEach((fn) => {
    try {
      fn({ user });
    } catch {
      /* ignore subscriber errors */
    }
  });
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    if (data.token) {
      localStorage.setItem('access_token', data.token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
    }
    const next = { id: data.id, email: data.email, name: data.name, phone: data.phone, role: data.role };
    setUser(next);
    notifyAuthChange(next);
    return data;
  };

  const register = async (email, password, name, phone) => {
    // Register no longer auto-logs the user in: it returns { status: 'pending_verification' }
    // because the user must confirm their email first.
    const { data } = await axios.post(`${API}/auth/register`, { email, password, name, phone });
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch {
      /* empty */
    }
    localStorage.removeItem('access_token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    notifyAuthChange(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
